/**
 * src/components/ui.jsx — the handful of presentational pieces every page uses.
 *
 * Kept in one file because each is a few lines and splitting them across eight
 * files would make them harder to find, not easier. The larger Phase 3
 * component set lives in components/ui/ instead (Dialog, Toast, Tabs, form
 * fields, …) — this file keeps only what predates that and still earns its
 * simplicity. `Modal` moved out entirely (see components/ui/Dialog.jsx,
 * a native `<dialog>` replacing the old `<div role="dialog">`) and every
 * caller was updated to import Dialog directly, so nothing here re-exports
 * it — two names for the same thing would be worse than one import to change.
 */
import { StatusBadge } from './ui/StatusBadge.jsx';

/**
 * Coloured status pill. Thin wrapper around the real component
 * (components/ui/StatusBadge.jsx) so its ~7 existing callers
 * (`Pill status={...}`) needed no changes when the underlying component
 * gained an icon alongside colour+text (audit.md: "status is never
 * colour-only").
 *
 * @param {{ status: string|null|undefined }} props
 * @returns {JSX.Element|null}
 */
export function Pill({ status }) {
  return <StatusBadge status={status} />;
}

/**
 * Error banner showing the server's own message.
 *
 * `role="alert"` is the fix for audit.md's "error announcement: missing"
 * gap — React mounting this element (which only happens when `children` is
 * truthy, i.e. exactly when an error appears) is what a screen reader
 * announces; it was rendering silently before.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element|null}
 */
export function ErrorBanner({ children }) {
  if (!children) return null;
  return (
    <div className="alert alert-error" role="alert">
      {children}
    </div>
  );
}

/**
 * Placeholder for a list with nothing in it. Always says what would be here
 * and, where useful, how to add the first one — an empty table with no
 * explanation is the most common way a dashboard feels broken.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function Empty({ children }) {
  return <div className="empty">{children}</div>;
}

/**
 * Page heading with an optional action on the right.
 *
 * @param {{ title: string, subtitle?: string, actions?: React.ReactNode, className?: string }} props
 * @returns {JSX.Element}
 */
export function PageHeader({ title, subtitle, actions, className }) {
  return (
    <div className={`row-between${className ? ` ${className}` : ''}`} style={{ marginBottom: 20 }}>
      <div>
        <h1>{title}</h1>
        {subtitle ? <p className="muted small">{subtitle}</p> : null}
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </div>
  );
}

/**
 * Loading and error wrapper for a page's main fetch.
 *
 * Saves every page writing the same three-way branch, and makes sure a failed
 * fetch always shows the server's message rather than an empty page.
 *
 * @param {{ loading: boolean, error: string|null, children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function Loader({ loading, error, children }) {
  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (loading) return <div className="empty loading-pulse">Loading…</div>;
  return children;
}
